/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useQuery } from '@tanstack/react-query';
import { OpenAPIHousien } from '@services/requests/core/OpenAPI';
import { request as __request } from '@services/requests/core/request';

interface AgencySiteSlugResult {
  slug: string;
  isLoading: boolean;
}

/**
 * Fetches the agency's public site slug directly from the backend --
 * kept self-contained (rather than depending on the agency-site-config
 * feature) since only the slug is needed here, for building a property's
 * public share link.
 * @param agencyId - The agency whose site slug is loaded.
 * @returns The agency site slug query result.
 */
export function useAgencySiteSlug(agencyId: string): AgencySiteSlugResult {
  const query = useQuery({
    queryKey: ['agency-site', agencyId, 'config'] as const,
    queryFn: () =>
      __request<{ slug?: string }>(OpenAPIHousien, {
        method: 'GET',
        url: '/agencies/{agencyId}/site/config',
        path: { agencyId },
      }),
    enabled: Boolean(agencyId),
    staleTime: 5 * 60 * 1000,
  });

  return {
    slug: query.data?.slug ?? '',
    isLoading: query.isLoading,
  };
}
